import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { BetterAuthError } from "../utils";
import { mcpFetch } from "./mcpFetch";
import { MCP_CLIENTS_QUERY_KEY } from "./useMcpClients";

export type RevokeMcpClientInput = {
  clientId: string;
};

/**
 * Withdraws a credential. The client row and its stored tokens go with it, so the connection stops
 * working — though an access token already issued is a signed JWT rather than a database lookup, so
 * it keeps working until it expires, within 15 minutes.
 */
export function useRevokeMcpClient(
  options?: UseMutationOptions<unknown, BetterAuthError, RevokeMcpClientInput, unknown>,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options ?? {};

  return useMutation({
    ...rest,
    mutationFn: async ({ clientId }: RevokeMcpClientInput) =>
      mcpFetch(
        "/oauth2/delete-client",
        z.unknown(),
        { method: "POST", body: { client_id: clientId } },
        "Could not revoke the credential",
      ),
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: MCP_CLIENTS_QUERY_KEY,
        refetchType: "all",
      });
      onSuccess?.(...args);
    },
  });
}
